/**
 * API Type Definitions
 * 
 * This module provides type definitions for the API layer, including response types,
 * error classes, request/response validation, and endpoint configuration. It ensures
 * type safety and consistency across the API implementation.
 * 
 * Features:
 * - API version types
 * - Response type definitions
 * - Error class hierarchy
 * - Request/response validation types
 * - Endpoint configuration types
 */

import { z } from 'zod';

/**
 * Supported API versions
 * Used for versioning API endpoints and responses
 */
export type ApiVersion = 'v1' | 'v2';

/**
 * Base API response structure
 * @template T The type of data contained in the response
 * 
 * @example
 * interface ApiResponse<User> {
 *   data: User;
 *   meta?: {
 *     version: 'v1';
 *     timestamp: '2024-01-01T00:00:00Z';
 *     requestId: 'abc-123';
 *   };
 * }
 */
export interface ApiResponse<T> {
  /** The response payload */
  data: T;
  /** Optional metadata about the response */
  meta?: {
    /** API version that handled the request */
    version: ApiVersion;
    /** ISO timestamp of when the response was generated */
    timestamp: string;
    /** Unique identifier for the request */
    requestId: string;
  };
}

/**
 * Base API error class
 * Extends Error with additional API-specific properties
 * 
 * @example
 * throw new ApiError(500, 'INTERNAL_ERROR', 'Something went wrong');
 */
export class ApiError extends Error {
  constructor(
    /** HTTP status code */
    public statusCode: number,
    /** Error code for client handling */
    public code: string,
    /** Human-readable error message */
    message: string,
    /** Additional error details */
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Validation error class
 * Used when request validation fails
 * 
 * @example
 * throw new ValidationError('Invalid email format', { field: 'email' });
 */
export class ValidationError extends ApiError {
  constructor(message: string, details?: unknown) {
    super(400, 'VALIDATION_ERROR', message, details);
    this.name = 'ValidationError';
  }
}

/**
 * Authentication error class
 * Used when authentication fails
 * 
 * @example
 * throw new AuthenticationError('Invalid credentials');
 */
export class AuthenticationError extends ApiError {
  constructor(message: string) {
    super(401, 'AUTHENTICATION_ERROR', message);
    this.name = 'AuthenticationError';
  }
}

/**
 * Authorization error class
 * Used when authorization fails
 * 
 * @example
 * throw new AuthorizationError('Insufficient permissions');
 */
export class AuthorizationError extends ApiError {
  constructor(message: string) {
    super(403, 'AUTHORIZATION_ERROR', message);
    this.name = 'AuthorizationError';
  }
}

/**
 * Not found error class
 * Used when a requested resource doesn't exist
 * 
 * @example
 * throw new NotFoundError('User not found');
 */
export class NotFoundError extends ApiError {
  constructor(message: string) {
    super(404, 'NOT_FOUND', message);
    this.name = 'NotFoundError';
  }
}

/**
 * Rate limit error class
 * Used when API rate limits are exceeded
 * 
 * @example
 * throw new RateLimitError('Too many requests', 60);
 */
export class RateLimitError extends ApiError {
  constructor(message: string, retryAfter?: number) {
    super(429, 'RATE_LIMIT_EXCEEDED', message, { retryAfter });
    this.name = 'RateLimitError';
  }
}

/**
 * Validated request structure
 * Contains parsed and validated request data
 * @template T The type of request body data
 * 
 * @example
 * interface ValidatedRequest<CreateUser> {
 *   body: CreateUser;
 *   params: { id: string };
 *   query: { include: string };
 *   headers: { authorization: string };
 * }
 */
export interface ValidatedRequest<T> {
  /** Validated request body */
  body: T;
  /** URL path parameters */
  params: Record<string, string>;
  /** URL query parameters */
  query: Record<string, string>;
  /** Request headers */
  headers: Record<string, string>;
}

/**
 * Validated response structure
 * Contains the response data and metadata
 * @template T The type of response data
 * 
 * @example
 * interface ValidatedResponse<User> {
 *   statusCode: 200;
 *   body: ApiResponse<User>;
 *   headers: { 'content-type': 'application/json' };
 * }
 */
export interface ValidatedResponse<T> {
  /** HTTP status code */
  statusCode: number;
  /** Response body with data and metadata */
  body: ApiResponse<T>;
  /** Response headers */
  headers: Record<string, string>;
}

/**
 * API endpoint configuration
 * Defines the structure and behavior of an API endpoint
 * @template TRequest The type of request data
 * @template TResponse The type of response data
 * 
 * @example
 * const endpoint: ApiEndpointConfig<CreateUser, User> = {
 *   path: '/users',
 *   method: 'POST',
 *   version: 'v1',
 *   requestSchema: createUserSchema,
 *   responseSchema: userSchema,
 *   handler: async (req) => {
 *     // Handle request
 *   },
 *   middleware: [authenticate, validate],
 * };
 */
export interface ApiEndpointConfig<TRequest, TResponse> {
  /** Endpoint path */
  path: string;
  /** HTTP method */
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  /** API version */
  version: ApiVersion;
  /** Optional schema for validating request data */
  requestSchema?: z.ZodType<TRequest>;
  /** Schema for validating response data */
  responseSchema: z.ZodType<TResponse>;
  /** Request handler function */
  handler: (request: ValidatedRequest<TRequest>) => Promise<ValidatedResponse<TResponse>>;
  /** Optional middleware functions */
  middleware?: Array<(req: ValidatedRequest<TRequest>) => Promise<void>>;
} 